import React, { useState } from "react";
import { Banner, FormLayout, TextField, Select, Icon, Button, Collapsible, Card, Text, Stack } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { ChevronDownMinor, ChevronUpMinor } from "@shopify/polaris-icons";
import contentTypeOptions from "./contentTypeOptions";

export default function FieldsForm(props) {
  const { t } = useTranslation();
  const [showTranslations, setShowTranslations] = useState({});
  const errors = props.errors || {};
  
  function handleFieldChange(key, value) {
    let newField = { ...props.field };
    newField[key] = value;
    props.setField(newField);
  }

  function handleTranslationChange(fieldName, locale, value) {
    let newField = { ...props.field };
    
    // Deep clone the translations object to ensure change detection works
    const newTranslations = newField.translations ? JSON.parse(JSON.stringify(newField.translations)) : {};
    
    if (!newTranslations[fieldName]) {
      newTranslations[fieldName] = {};
    }
    
    newTranslations[fieldName][locale] = value;
    newField.translations = newTranslations;
    
    props.setField(newField);
  }

  function toggleTranslationVisibility(fieldName) {
    setShowTranslations(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  }

  function getTranslationValue(fieldName, locale) {
    return props.field?.translations?.[fieldName]?.[locale] || "";
  }

  function getPlaceholderValue(fieldName, locale) {
    if (locale === props.field?.current_locale) return "";
    return props.field?.[fieldName] || t("settings.customFields.form.translations.defaultValue", { value: props.field?.[fieldName] || "field name" });
  }

  // Helper function to get locale display name from Rails data
  function getLocaleDisplayName(localeCode) {
    const locale = availableLocales.find(l => l.code === localeCode);
    return locale ? locale.name : localeCode;
  }

  if (props.field == null) {
    return null;
  }

  const isLocalizationEnabled = props.field?.localization_enabled;
  const availableLocales = props.field?.available_locales || [{ code: "en", name: "English" }];
  const currentLocale = props.field?.current_locale || "en";
  const nonDefaultLocales = availableLocales.filter(locale => locale.code !== currentLocale);

  const formatError = (key) => {
    if (!errors[key]) return undefined;
    return errors[key].join(" ");
  };

  return (
    <>
      {errors.base && (
        <Banner status="critical">
          <p>{errors.base.join(" ")}</p>
        </Banner>
      )}
    <FormLayout>
      <TextField
        label={t("settings.customFields.form.name")}
        value={props.field.name}
        onChange={(v) => handleFieldChange("name", v)}
        error={formatError("name")}
      />
      
      {/* Translation fields for name */}
      {isLocalizationEnabled && nonDefaultLocales.length > 0 && (
        <Card>
          <Card.Section>
            <Stack vertical spacing="loose">
              <Stack alignment="center" distribution="equalSpacing">
                <Text variant="headingMd" as="h3">{t("settings.customFields.form.translations.title")}</Text>
                <Button
                  plain
                  icon={showTranslations.name ? ChevronUpMinor : ChevronDownMinor}
                  onClick={() => toggleTranslationVisibility('name')}
                >
                  {showTranslations.name ? t("settings.customFields.form.translations.hideTranslations") : t("settings.customFields.form.translations.showTranslations")}
                </Button>
              </Stack>
              
              <Text variant="bodyMd" color="subdued">
                {t("settings.customFields.form.translations.description", { language: getLocaleDisplayName(currentLocale) })}
              </Text>
              
              <Collapsible open={showTranslations.name}>
                <Stack vertical spacing="tight">
                  {nonDefaultLocales.map(locale => (
                    <TextField
                      key={`name-${locale.code}`}
                      label={t("settings.customFields.form.translations.nameTranslation", { language: getLocaleDisplayName(locale.code) })}
                      value={getTranslationValue('name', locale.code)}
                      onChange={(value) => handleTranslationChange('name', locale.code, value)}
                      placeholder={getPlaceholderValue('name', locale.code)}
                      helpText={t("settings.customFields.form.translations.defaultValue", { value: props.field.name || "field name" })}
                    />
                  ))}
                </Stack>
              </Collapsible>
            </Stack>
          </Card.Section>
        </Card>
      )}
      
      <Select
        label={t("settings.customFields.form.contentType")}
        options={contentTypeOptions(t)}
        value={props.field.content_type}
        onChange={(v) => handleFieldChange("content_type", v)}
        error={formatError("content_type")}
      />
      
      {props.field.name && props.field.handle && (
        <TextField
          label={t("settings.customFields.form.key")}
          value={props.field.handle}
          monospaced
          disabled
          prefix={t("settings.customFields.form.keyPrefix")}
          helpText={t("settings.customFields.form.keyHelp", { name: props.field.name.toLowerCase() })}
        />
      )}
    </FormLayout>
  </>
  );
}
